import { useState } from "react";
import styles from "./DealerAnalytics.module.scss";
import { BarList, Heatmap, StateTileMap } from "./charts";
import { STATE_NAMES } from "./usStates";
import { Card, Delta, Empty, ReportFrame, Segmented, SortTable, StatTile, fmtNum, pct, useReport } from "./shared";

const WWW = "https://www.importjdmmagic.com";
const WEEKDAYS = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
const TYPE_LABELS = { inquiry: "Inquiry", quote: "Quote", inspection: "Inspection request", contact: "Contact form" };
const MAP_METRICS = [
  { value: "leads", label: "Leads" },
  { value: "saves", label: "Saves" },
  { value: "views", label: "Car views" },
];

const hourName = (h) => new Date(2000, 0, 1, h).toLocaleTimeString("en-US", { hour: "numeric" });

const Sources = ({ range, refreshKey }) => {
  const state = useReport("leads", range, refreshKey);
  const [mapMetric, setMapMetric] = useState("leads");

  return (
    <ReportFrame state={state}>
      {(d) => {
        const t = d.totals;
        const quoteTotal = d.quotes.shipped + d.quotes.imported;
        const stateValues = Object.fromEntries(d.states.map((s) => [s.state, s[mapMetric]]));
        const topStates = [...d.states].sort((a, b) => b[mapMetric] - a[mapMetric]).filter((s) => s[mapMetric] > 0).slice(0, 10);
        const slots = [];
        d.heatmap.forEach((row, day) => row.forEach((n, hour) => n && slots.push({ day, hour, n })));
        slots.sort((a, b) => b.n - a.n);

        return (
          <div className={styles.stack}>
            <div className={styles.tiles}>
              <StatTile label="Leads" value={fmtNum(t.leads)} delta={<Delta current={t.leads} previous={t.previous} />} sub={`${fmtNum(d.origins.www)} from www · ${fmtNum(d.origins.portal)} from the portal`} />
              <StatTile label="Leads that bought" value={fmtNum(t.converted)} sub={`${pct(t.converted, t.leads)} of leads became a dealer sale`} />
              <StatTile label="Guests" value={pct(d.accounts.guest, t.leads, 0)} sub={`${fmtNum(d.accounts.guest)} leads with no account`} />
              <StatTile label="Quote requests" value={fmtNum(quoteTotal)} sub={`${fmtNum(d.quotes.imported)} imported (all-in) · ${fmtNum(d.quotes.shipped)} shipped`} />
            </div>

            <Card
              title="Where visitors and leads come from"
              sub="The first touch of the visit, as www records it (the portal records none, so its leads read Not recorded). Sales are distinct dealer sales matched to a lead from that channel."
            >
              <SortTable
                rowKey={(r) => r.channel}
                rows={d.channels}
                initialSort={{ key: "leads", dir: "desc" }}
                columns={[
                  { key: "channel", label: "Channel" },
                  { key: "views", label: "Car views", num: true, bar: true },
                  { key: "visitors", label: "Visitors", num: true },
                  { key: "leads", label: "Leads", num: true, bar: true },
                  { key: "previous", label: "Previous", num: true, render: (r) => <>{fmtNum(r.previous)}{r.previous > 0 && <> <Delta current={r.leads} previous={r.previous} suffix="" /></>}</> },
                  { key: "sales", label: "Sales", num: true },
                  { key: "viewRate", label: "Views → lead", num: true, sort: (r) => (r.views ? r.leads / r.views : -1), render: (r) => (r.views ? pct(r.leads, r.views) : "–") },
                  { key: "saleRate", label: "Lead → sale", num: true, sort: (r) => (r.leads ? r.sales / r.leads : -1), render: (r) => pct(r.sales, r.leads) },
                  {
                    key: "sources",
                    label: "Top sources",
                    sortable: false,
                    render: (r) => r.sources.map((s) => <span key={s.source} className={styles.chip} style={{ margin: "0 4px 2px 0" }}>{s.source} · {s.count}</span>),
                  },
                ]}
              />
            </Card>

            <Card
              title="Entry pages"
              sub="The page a visit began on, for visits that went on to view cars or send a lead. The SEO view: which pages bring browsers, and which bring buyers."
            >
              <SortTable
                rowKey={(r) => r.path}
                rows={d.landingPages}
                initialSort={{ key: "leads", dir: "desc" }}
                columns={[
                  { key: "path", label: "Landing page", className: styles.pathCell, render: (r) => <a className={styles.link} href={`${WWW}${r.path}`} target="_blank" rel="noopener noreferrer">{r.path}</a> },
                  { key: "views", label: "Car views", num: true, bar: true },
                  { key: "visitors", label: "Visitors", num: true },
                  { key: "leads", label: "Leads", num: true, bar: true },
                  { key: "sales", label: "Sales", num: true },
                  { key: "viewRate", label: "Views → lead", num: true, sort: (r) => (r.views ? r.leads / r.views : -1), render: (r) => (r.views ? pct(r.leads, r.views) : "–") },
                ]}
              />
            </Card>

            <div className={styles.grid2}>
              <Card
                title="Where people are"
                sub="US states from the visitor's network address, so approximate. Hover a state for its count."
                actions={<Segmented label="Measure" options={MAP_METRICS} value={mapMetric} onChange={setMapMetric} />}
              >
                <StateTileMap values={stateValues} unit={MAP_METRICS.find((m) => m.value === mapMetric).label.toLowerCase()} />
                <div style={{ marginTop: 16 }}>
                  <BarList
                    rows={topStates.map((s) => ({ key: s.state, label: STATE_NAMES[s.state] || s.state, value: s[mapMetric] }))}
                    empty="No US locations in this period."
                  />
                </div>
                {d.countries.filter((c) => c.country !== "US").length > 0 && (
                  <p className={`${styles.small} ${styles.muted}`} style={{ marginTop: 12 }}>
                    Outside the US: {d.countries.filter((c) => c.country !== "US").map((c) => `${c.country} ${fmtNum(c[mapMetric])}`).join(" · ")}
                  </p>
                )}
              </Card>
              <Card title="When leads arrive" sub="New leads by weekday and hour, US Eastern.">
                <Heatmap grid={d.heatmap} unit="leads" />
                <p className={`${styles.small} ${styles.muted}`} style={{ marginTop: 12 }}>
                  Busiest: {slots.slice(0, 4).map((s) => `${WEEKDAYS[s.day]} ${hourName(s.hour)} (${s.n})`).join(" · ") || "–"}
                </p>
              </Card>
            </div>

            <div className={styles.grid3}>
              <Card title="What they ask for">
                <BarList rows={Object.entries(d.types).sort((a, b) => b[1] - a[1]).map(([k, n]) => ({ key: k, label: TYPE_LABELS[k] || k, value: n, note: pct(n, t.leads, 0) }))} />
              </Card>
              <Card title="Quote ports" sub={`${fmtNum(d.quotes.withTitle)} of ${fmtNum(quoteTotal)} quotes asked to include the title.`}>
                {d.quotes.ports.length
                  ? <BarList rows={d.quotes.ports.map((p) => ({ key: p.port, label: p.port, value: p.count }))} />
                  : <Empty>No quotes in this period.</Empty>}
              </Card>
              <Card title="Accounts">
                <BarList rows={[
                  { label: "Guest (no account)", value: d.accounts.guest, note: pct(d.accounts.guest, t.leads, 0) },
                  { label: "Signed in", value: d.accounts.account, note: pct(d.accounts.account, t.leads, 0) },
                  { label: "From www", value: d.origins.www, note: pct(d.origins.www, t.leads, 0) },
                  { label: "From the portal", value: d.origins.portal, note: pct(d.origins.portal, t.leads, 0) },
                ]} />
              </Card>
            </div>
          </div>
        );
      }}
    </ReportFrame>
  );
};

export default Sources;
